import { forwardRef, useImperativeHandle } from "react";
import { Box, Space, Text, Title } from "@mantine/core";
import { Controller, useForm } from "react-hook-form";
import { ButtonsSelect } from "../../button/ButtonsSelect";
import { useMutation } from "@apollo/client";
import { UPDATE_REMOVABLE_ITEM } from "../../../gql/items";
import { Product } from "../../../types/interfaces";
import { RemovableItemOrderType } from "../../../types/enums";
import { PartialDentureOrderTypeChoices } from "../../../types/constants";

type OrderTypeChoiceFormData = {
  partialDentureOrderType: RemovableItemOrderType;
};

type CreateRemovableItemPartialDentureOrderTypeProps = {
  product: Product;
};

export interface PartialDentureOrderTypeChoiceRef {
  saveData: () => Promise<void>;
}

export const CreateRemovableItemPartialDentureOrderTypeChoice = forwardRef<
  PartialDentureOrderTypeChoiceRef,
  CreateRemovableItemPartialDentureOrderTypeProps
>(({ product }, ref) => {
  const removableItems = product.removableItem ?? [];

  const {
    control,
    watch,
    formState: { errors },
  } = useForm<OrderTypeChoiceFormData>({
    defaultValues: {
      partialDentureOrderType:
        (removableItems[0]
          ?.partialDentureOrderType as RemovableItemOrderType) ??
        RemovableItemOrderType.FINAL,
    },
  });

  const [updateRemovableItem] = useMutation(UPDATE_REMOVABLE_ITEM);

  const partialDentureOrderType = watch("partialDentureOrderType");

  const saveData = async (): Promise<void> => {
    if (!removableItems.length || !partialDentureOrderType) return;

    // update all removableItems ( 1 per arcade)
    await Promise.all(
      removableItems.map(item =>
        updateRemovableItem({
          variables: {
            where: { id: item.id },
            data: {
              partialDentureOrderType: { set: partialDentureOrderType },
              rdvNumber: { set: 1 },
            },
          },
        })
      )
    );
  };

  useImperativeHandle(ref, () => ({ saveData }));

  if (!removableItems.length) {
    return (
      <div>
        <Text>
          {"Erreur sur l'ajout de produit. Veuillez contacter le support."}
        </Text>
      </div>
    );
  }

  return (
    <Box>
      <Title order={3}>Que souhaitez-vous commander ?</Title>
      <Space h="md" />
      <Controller
        name="partialDentureOrderType"
        rules={{ required: true }}
        control={control}
        render={({ field }) => (
          <ButtonsSelect
            data={PartialDentureOrderTypeChoices}
            value={field.value}
            onChange={value => field.onChange(value)}
            numberOfColumns={1}
            align="left"
          />
        )}
      />
      {errors?.partialDentureOrderType?.type === "required" && (
        <Text size="sm" style={{ color: "red" }}>
          Veuillez sélectionner le type de commande
        </Text>
      )}
      <Space h="xl" />
    </Box>
  );
});

CreateRemovableItemPartialDentureOrderTypeChoice.displayName =
  "CreateRemovableItemPartialDentureOrderTypeChoice";
